/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import cn from "classnames";
import { ArrowDown } from "components/Icons";
import LoadingDots from "components/LoadingDots";
import { ChangeEvent, FC, KeyboardEvent, useState } from "react";
import { Stack, Input, Text } from "components";
import { useRef } from "react";
import useDetectClickOutside from "utils/useDetectClickOutside";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

type IOption = {
  id: string;
  value: string;
  label: string;
};

type Props = {
  disabled?: boolean;
  isLoading?: boolean;
  onChange: (option: IOption) => void;
  options: IOption[];
  value: string;
  isAsync?: boolean;
  onSearch: (value: string) => void;
  isMissing?: boolean;
  label?: string;
  required?: boolean;
};

const SearchableSelect: FC<Props> = ({
  disabled,
  isLoading,
  onChange,
  options,
  value,
  isAsync = true,
  onSearch,
  isMissing,
  label,
  required,
}) => {
  const boxRef = useRef(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const prevSearch = useRef("");
  useDetectClickOutside(boxRef, () => setOpen(false));
  const [isOpen, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<IOption | null>(null);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<IOption[]>([]);

  const { t } = useTranslation();

  const handleOnSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const search = e.target.value;
    setSearch(search);
    setOpen(true);
    if (isAsync) return;
  };

  const onSelect = (option: IOption) => {
    if (option.id === selectedOption?.id) return;
    setSelectedOption(option);
    setOpen(false);
    if (onChange) {
      onChange(option);
    }
  };
  useEffect(() => {
    if (options && options.length > 0) {
      setItems(options);
    } else {
      setItems([]);
    }
    if (value && value.length > 0) {
      const selected = options?.find(
        (option) =>
          option.value === value ||
          option.label === value ||
          option.id === value,
      );
      setSelectedOption(selected || null);
    } else {
      setSelectedOption(null);
    }
  }, [options, value]);
  const [isFetching, setFetching] = useState(false);
  return (
    <div ref={boxRef}>
      <Stack
        className={cn("relative cursor-pointer")}
        onClick={() => {
          if (disabled) return;
          setOpen(!isOpen);
        }}
      >
        <Input
          disabled={disabled}
          placeholder={
            selectedOption ? "" : t("forms.placeholders.searchForAnAirportName")
          }
          type="text"
          value={!isOpen ? "" : search}
          error={isMissing}
          onChange={handleOnSearch}
          onKeyUp={(e: KeyboardEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement;
            clearTimeout(timeoutRef.current);
            prevSearch.current = target.value;

            if (target.value === "" || target.value.length <= 2) {
              clearTimeout(timeoutRef.current);
              setItems([]);
              setFetching(false);
              setSelectedOption(null);
              onChange({ value: "", id: "", label: "" });
              return;
            }
            if (!isAsync || target.value.length <= 2) return;
            if (e.key === "Enter") {
              if (onSearch) {
                setFetching(false);
                return onSearch(target.value);
              }
            }
            setFetching(false);
            clearTimeout(timeoutRef.current);

            setFetching(true);
            timeoutRef.current = setTimeout(async () => {
              if (onSearch) {
                await onSearch(target.value);
              }
              setFetching(false);
            }, 1000);
          }}
          label={label}
          required={required}
        />
        {!isOpen && selectedOption && (
          <div className="absolute start-3.5 top-11 -text--component-colors-field-default-input-text line-clamp-1 ">
            <Text className="me-10">{selectedOption.label}</Text>
          </div>
        )}
        <div
          className={cn("absolute end-16 top-[53px]", {
            hidden: !isLoading && !isFetching,
          })}
        >
          <LoadingDots />
        </div>
        <div className="absolute end-3 top-[53px] ">
          <ArrowDown />
        </div>
      </Stack>
      <Stack className="relative">
        <Stack
          as="ul"
          className={cn(
            "-bg--theme-colors-surface-neutral-level02 border-t-0 border-primary-400 transition-all duration-200 absolute z-[1] list-none",
            isOpen
              ? "max-h-64 overflow-auto border py-1"
              : "max-h-0 border-0  overflow-hidden",
          )}
        >
          {items.map((item, i) => (
            <li
              key={i}
              className={cn(
                "py-1 px-3 flex justify-between -text--component-colors-field-default-input-text hover:-text--component-colors-field-hover-input-text hover:-bg--theme-colors-surface-neutral-level03",
              )}
              role="button"
              onClick={() => onSelect(item)}
            >
              <Text>{item.label}</Text>
              <Text>
                {selectedOption && selectedOption.id === item.id && "✓"}
              </Text>
            </li>
          ))}
        </Stack>
      </Stack>
    </div>
  );
};

export default SearchableSelect;
